import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Minus, Plus, ArrowLeft, ShieldCheck, Truck, ChevronRight, FileCheck2 } from "lucide-react";
import { api, resolveUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Seo } from "@/components/Seo";
import { ProductVial } from "@/components/ProductVial";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    setQty(1);
    api.get(`/products/${slug}`).then(({ data }) => setProduct(data)).catch(() => setProduct(false));
  }, [slug]);

  if (product === false) return <div className="mx-auto max-w-7xl px-5 py-32 text-center text-brand-navy/60">Product not found.</div>;
  if (!product) return <div className="mx-auto max-w-7xl px-5 py-32 text-center text-brand-navy/40">Loading…</div>;

  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.short || product.description,
    image: [resolveUrl(product.image)].filter(Boolean),
    sku: product.slug,
    brand: { "@type": "Brand", name: "Just Peptides Supply" },
    category: product.category,
    offers: {
      "@type": "Offer",
      url: `https://www.justpeptidessupply.com/product/${product.slug}`,
      priceCurrency: "USD",
      price: Number(product.price).toFixed(2),
      availability:
        product.stock === 0 ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
    },
  };

  return (
    <>
      <Seo
        title={`${product.name} | Just Peptides Supply`}
        description={product.short}
        path={`/product/${product.slug}`}
        image={resolveUrl(product.image)}
        ogType="product"
        jsonLd={productJsonLd}
      />

      <div className="mx-auto max-w-7xl px-5 lg:px-8 pt-8">
        <div className="flex items-center gap-1.5 text-sm text-brand-navy/50 font-mono-label">
          <Link to="/shop" className="hover:text-brand-blue flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Shop</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-brand-navy/70">{product.name}</span>
        </div>
      </div>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-10 grid gap-12 lg:grid-cols-2">
        <div className="rounded-2xl bg-gradient-to-br from-brand-mist to-white border border-brand-navy/10 flex items-center justify-center p-6 aspect-square">
          <ProductVial product={product} className="w-full h-full" data-testid="product-image" />
        </div>

        <div>
          <p className="font-mono-label text-xs uppercase tracking-[0.2em] text-brand-blue">{product.category}</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl text-brand-navy tracking-tight" data-testid="product-title">{product.name}</h1>
          <p className="mt-4 font-display text-4xl text-brand-navy" data-testid="product-price">${product.price.toFixed(2)}</p>

          <p className="mt-6 text-brand-navy/65 leading-relaxed whitespace-pre-line">{product.description}</p>

          {product.benefits && product.benefits.length > 0 && (
            <div className="mt-7" data-testid="product-benefits">
              <p className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-brand-blue">Reported Benefits</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {product.benefits.map((b) => (
                  <span key={b} className="inline-flex items-center gap-1.5 rounded-full border border-brand-navy/10 bg-brand-mist px-3 py-1.5 text-sm text-brand-navy">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" /> {b}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-7 grid grid-cols-2 gap-3">
            <Spec label="Size" value={product.size} />
            <Spec label="Purity" value={product.purity} />
            <Spec label="Form" value="Lyophilized powder" />
            <Spec label="Origin" value="Domestic (USA)" />
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-brand-navy/15 rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3" data-testid="qty-decrease"><Minus className="h-4 w-4" /></button>
              <span className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => Math.min((product.stock ?? 0), q + 1))} className="p-3" data-testid="qty-increase"><Plus className="h-4 w-4" /></button>
            </div>
            <Button
              data-testid="detail-add-to-cart"
              disabled={(product.stock ?? 0) <= 0}
              onClick={() => { addItem(product, qty); toast.success(`${product.name} added to cart`); }}
              className="flex-1 rounded-full bg-brand-navy hover:bg-brand-teal text-white h-14 text-base disabled:bg-brand-navy/20 disabled:text-brand-navy/40"
            >
              {(product.stock ?? 0) <= 0 ? "Sold Out" : `Add to Cart · $${(product.price * qty).toFixed(2)}`}
            </Button>
          </div>
          {(product.stock ?? 0) > 0 && (product.stock ?? 0) <= 5 && (
            <p className="mt-3 text-sm font-semibold text-orange-500" data-testid="low-stock">Only {product.stock} left in stock</p>
          )}
          {(product.stock ?? 0) <= 0 && (
            <p className="mt-3 text-sm font-semibold text-destructive" data-testid="sold-out-detail">Currently sold out — check back soon.</p>
          )}

          <div className="mt-6 flex flex-wrap gap-5 text-sm text-brand-navy/60">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-teal" /> High-purity verified</span>
            <span className="flex items-center gap-2"><Truck className="h-4 w-4 text-brand-teal" /> Fast U.S. shipping</span>
          </div>

          {product.coa_url && (
            <a
              href={resolveUrl(product.coa_url)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="coa-link"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-brand-teal/40 bg-brand-teal/10 px-4 py-2 text-sm font-semibold text-brand-navy hover:bg-brand-teal/20 transition-colors"
            >
              <FileCheck2 className="h-4 w-4 text-brand-teal" /> View Certificate of Analysis (COA)
            </a>
          )}

          <p className="mt-8 font-mono-label text-[11px] uppercase tracking-[0.12em] text-brand-navy/40 leading-relaxed border-t border-brand-navy/10 pt-6">
            For research use only. Not for human consumption. Sold individually; does not include bacteriostatic water or syringes.
          </p>
        </div>
      </section>
    </>
  );
}

const Spec = ({ label, value }) => (
  <div className="rounded-lg border border-brand-navy/10 bg-white p-3">
    <p className="font-mono-label text-[10px] uppercase tracking-[0.15em] text-brand-navy/45">{label}</p>
    <p className="mt-0.5 font-semibold text-brand-navy text-sm">{value}</p>
  </div>
);
